/**
 * « MODÈLES » — the design system v2 models of the album's theme, drawn with
 * the album's OWN photos (owner 2026-10-14: « I need to be able to use the new
 * models in here, and the images that I want »).
 *
 * One picker for both doors: the right panel « Outils — Page N » → Modèles and
 * the « Options de la page » sheet → Modèles. The old Canva templates
 * (layout_templates, all retired) never show here.
 *
 *   · the cards are the worker's builds (POST /themes/editor/:id/models) —
 *     drawn by ThemedPageView, exactly as the page prints, with the page's
 *     photos first, then the photos placed nowhere;
 *   · tabs by photo count 1–6, the page's own count first;
 *   · staff may switch theme (the worker sends the list only to them);
 *   · picking a card asks the worker to build that page for real
 *     (POST /themes/editor/:id/apply): a face that would be cut → its French
 *     refusal shows here and nothing changes;
 *   · « Double page » (2026-10-18, S5 « Panorama »): one wide photo across
 *     this page and the one facing it — the card shows both halves, says which
 *     pages it takes and how many photos go back to « non placées ».
 */
import { useEffect, useMemo, useState } from 'react';
import { ThemedPageView } from '../page/ThemedPageView';
import { editorModelsApi, ModelRefusal, type EditorModelCard, type EditorModelsResponse, type EditorSpreadCard, type EditorThemedPage } from '../../lib/editorModelsApi';

export interface ThemeModelPickerProps {
  sessionId: string;
  /** Album page number (1-based). */
  pageNumber: number;
  /** The page's photos (its count opens the tabs). */
  pagePhotoIds: string[];
  /** Photos placed nowhere (after the page's own in the pool). */
  unplacedIds: string[];
  /** Photos other pages may lend when a model needs more — photo id → its page. */
  lendable: Array<{ id: string; page: number }>;
  /** Decoration images the album's other pages carry. */
  usedImages: string[];
  albumPages: number;
  side: 'left' | 'right';
  /** The page's theme when it already is a themed page (opens on it). */
  pageTheme?: string | null;
  photoUrl: (photoId: string) => string | null;
  onApplied: (page: EditorThemedPage, photoIds: string[]) => void;
  /** A two-page model applied: album pages `leftPage` and `rightPage` become its halves. */
  onAppliedSpread?: (left: EditorThemedPage, right: EditorThemedPage, leftPage: number, rightPage: number, photoIds: string[]) => void;
  /** Photos on a page (album page number → its photos), to say what a « Double page » sends back to « non placées ». */
  photosOnPage?: (page: number) => string[];
  onClose: () => void;
}

const COUNTS = [1, 2, 3, 4, 5, 6] as const;
/** The « Double page » tab's key (two-page models). */
const SPREAD_TAB = 0;

export function ThemeModelPicker(p: ThemeModelPickerProps) {
  const [theme, setTheme] = useState<string | null>(p.pageTheme ?? null);
  const [data, setData] = useState<EditorModelsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [applying, setApplying] = useState<string | null>(null);
  const own = Math.min(6, Math.max(1, p.pagePhotoIds.filter(Boolean).length || 1));
  const [count, setCount] = useState<number>(own);

  const pool = useMemo(() => {
    const seen = new Set<string>();
    return [...p.pagePhotoIds, ...p.unplacedIds, ...p.lendable.map((l) => l.id)].filter((id) => id && !seen.has(id) && (seen.add(id), true));
  }, [p.pagePhotoIds, p.unplacedIds, p.lendable]);
  const lentBy = useMemo(() => new Map(p.lendable.map((l) => [l.id, l.page])), [p.lendable]);

  useEffect(() => {
    let off = false;
    setLoading(true); setError(null);
    editorModelsApi.models(p.sessionId, {
      page: p.pageNumber, pool_ids: pool, theme, side: p.side, used_images: p.usedImages, album_pages: p.albumPages,
    }).then((d) => { if (!off) { setData(d); setLoading(false); } })
      .catch((e) => { if (!off) { setError((e as Error).message || 'Erreur'); setLoading(false); } });
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.sessionId, p.pageNumber, theme]);

  // The page's own count first, then the others.
  const spreads = data?.spreads ?? [];
  const tabs = useMemo(() => [own, ...COUNTS.filter((n) => n !== own), ...(spreads.length ? [SPREAD_TAB] : [])], [own, spreads.length]);
  const cards = count === SPREAD_TAB ? [] : (data?.models ?? []).filter((m) => m.count === count);
  const usable = (n: number) => (n === SPREAD_TAB ? spreads.some((m) => m.ok) : (data?.models ?? []).some((m) => m.count === n && m.ok));

  const pickSpread = async (m: EditorSpreadCard) => {
    if (!m.ok || !m.photo_ids || applying) { if (!m.ok) setRefusal(m.message ?? null); return; }
    setApplying(m.code); setRefusal(null);
    try {
      const res = await editorModelsApi.apply(p.sessionId, {
        page: p.pageNumber, model: m.code, photo_ids: m.photo_ids, theme: data?.theme.slug, side: p.side,
        used_images: p.usedImages, album_pages: p.albumPages,
      });
      if (!res.spread) throw new Error('no spread');
      p.onAppliedSpread?.(res.spread.left, res.spread.right, res.spread.left_page, res.spread.right_page, m.photo_ids);
    } catch (e) {
      setRefusal(e instanceof ModelRefusal ? e.message : 'Le modèle n’a pas pu être appliqué. Réessayez.');
    } finally {
      setApplying(null);
    }
  };

  const pick = async (m: EditorModelCard) => {
    if (!m.ok || !m.photo_ids || applying) { if (!m.ok) setRefusal(m.message ?? null); return; }
    setApplying(m.code); setRefusal(null);
    try {
      const res = await editorModelsApi.apply(p.sessionId, {
        page: p.pageNumber, model: m.code, photo_ids: m.photo_ids, theme: data?.theme.slug, side: p.side,
        used_images: p.usedImages, album_pages: p.albumPages,
      });
      p.onApplied(res.page, m.photo_ids);
    } catch (e) {
      setRefusal(e instanceof ModelRefusal ? e.message : 'Le modèle n’a pas pu être appliqué. Réessayez.');
    } finally {
      setApplying(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center sm:p-4" onClick={p.onClose} data-theme-model-picker>
      <div className="fixed inset-0 bg-black/55 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              Modèles — page {p.pageNumber}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {data ? <>Thème <b className="text-gray-700">{data.theme.name}</b> · {data.colourway.name} · avec vos photos</> : 'Chargement des modèles…'}
            </p>
          </div>
          {data && data.themes.length > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 shrink-0">
              <span className="hidden sm:inline">Thème</span>
              <select
                aria-label="Thème"
                className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white"
                value={data.theme.slug}
                onChange={(e) => setTheme(e.target.value)}
              >
                {data.themes.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
              </select>
            </label>
          )}
          <button onClick={p.onClose} aria-label="Fermer" className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 shrink-0">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div className="shrink-0 px-4 sm:px-5 py-2.5 border-b border-gray-100 flex gap-1.5 overflow-x-auto" role="tablist" aria-label="Nombre de photos">
          {tabs.map((n) => (
            <button
              key={n}
              role="tab"
              aria-selected={count === n}
              onClick={() => { setCount(n); setRefusal(null); }}
              className={'shrink-0 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors '
                + (count === n ? 'bg-[#C9A04C] text-white' : usable(n) ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-50 text-gray-400')}
            >
              {n === SPREAD_TAB ? 'Double page' : <>{n} photo{n > 1 ? 's' : ''}{n === own ? ' · cette page' : ''}</>}
            </button>
          ))}
        </div>

        {refusal && (
          <div className="shrink-0 mx-4 sm:mx-5 mt-3 px-3 py-2 rounded-lg text-xs bg-amber-50 text-amber-900 border border-amber-200" role="alert">
            {refusal}
          </div>
        )}

        <div className="flex-1 overflow-auto p-4 sm:p-5">
          {loading && <p className="text-center text-sm text-gray-500 py-12">Chargement des modèles…</p>}
          {error && !loading && <p className="text-center text-sm text-red-600 py-12">Les modèles n’ont pas pu être chargés ({error}).</p>}
          {!loading && !error && count === SPREAD_TAB && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-spread-cards>
              {spreads.map((m) => {
                const back = [m.left_page, m.right_page].flatMap((n) => p.photosOnPage?.(n) ?? []).filter((id) => !(m.photo_ids ?? []).includes(id));
                return (
                  <button
                    key={m.code}
                    data-model-card={m.code}
                    onClick={() => pickSpread(m)}
                    disabled={!!applying}
                    className={'group relative rounded-xl border text-left overflow-hidden transition-all '
                      + (m.ok ? 'border-gray-200 hover:border-[#C9A04C] hover:shadow-md' : 'border-dashed border-gray-200 opacity-70')}
                  >
                    <div className="relative w-full flex" style={{ aspectRatio: '400 / 297', background: m.left?.blank_color ?? '#f5f2ec' }}>
                      {m.ok && m.left && m.right ? (
                        <>
                          <div className="relative h-full" style={{ width: '50%' }}><ThemedPageView page={m.left} photoUrl={p.photoUrl} /></div>
                          <div className="relative h-full" style={{ width: '50%' }}><ThemedPageView page={m.right} photoUrl={p.photoUrl} /></div>
                          <div className="absolute inset-y-0 left-1/2 w-px bg-black/10 pointer-events-none" />
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center p-3 text-center text-[11px] text-gray-500">
                          {m.message ?? 'Indisponible'}
                        </div>
                      )}
                      {applying === m.code && (
                        <div className="absolute inset-0 bg-white/60 flex items-center justify-center text-xs font-medium text-gray-700">Application…</div>
                      )}
                    </div>
                    <div className="px-2 py-1.5 bg-white border-t border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-900 truncate">{m.name} · {m.code}</p>
                      <p className="text-[10px] text-gray-500">
                        Une photo sur les pages {m.left_page} et {m.right_page}
                        {back.length > 0 && ` · ${back.length} photo${back.length > 1 ? 's' : ''} retourne${back.length > 1 ? 'nt' : ''} dans « non placées »`}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {!loading && !error && count !== SPREAD_TAB && cards.length === 0 && (
            <p className="text-center text-sm text-gray-500 py-12">Aucun modèle à {count} photo{count > 1 ? 's' : ''} dans ce thème.</p>
          )}
          {!loading && !error && cards.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
              {cards.map((m) => {
                const lent = (m.photo_ids ?? []).filter((id) => lentBy.has(id) && !p.pagePhotoIds.includes(id) && !p.unplacedIds.includes(id));
                return (
                  <button
                    key={m.code}
                    data-model-card={m.code}
                    onClick={() => pick(m)}
                    disabled={!!applying}
                    className={'group relative rounded-xl border text-left overflow-hidden transition-all '
                      + (m.ok ? 'border-gray-200 hover:border-[#C9A04C] hover:shadow-md' : 'border-dashed border-gray-200 opacity-70')}
                  >
                    <div className="relative w-full" style={{ aspectRatio: '200 / 297', background: m.page?.blank_color ?? '#f5f2ec' }}>
                      {m.ok && m.page ? (
                        <ThemedPageView page={m.page} photoUrl={p.photoUrl} />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center p-3 text-center text-[11px] text-gray-500">
                          {m.reason === 'face' ? 'Couperait un visage avec ces photos' : m.message ?? 'Indisponible'}
                        </div>
                      )}
                      {applying === m.code && (
                        <div className="absolute inset-0 bg-white/60 flex items-center justify-center text-xs font-medium text-gray-700">Application…</div>
                      )}
                    </div>
                    <div className="px-2 py-1.5 bg-white border-t border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-900 truncate">{m.name}</p>
                      <p className="text-[10px] text-gray-500 truncate">
                        {m.kind === 'decorated' ? 'Décoré' : 'Simple'} · {m.code}
                        {lent.length > 0 && ` · prend ${lent.length} photo${lent.length > 1 ? 's' : ''} de la page ${[...new Set(lent.map((id) => lentBy.get(id)))].join(', ')}`}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="shrink-0 px-4 sm:px-5 py-2.5 border-t border-gray-100 text-[11px] text-gray-500">
          Ensuite : touchez une photo pour la déplacer ou zoomer dans son cadre, ou la changer (« Changer la photo », ou glissez-en une sur le cadre). Annuler (↶) revient en arrière.
        </div>
      </div>
    </div>
  );
}
